import type { AnswerCard, AttachmentCard, CalendarCard, MailCardItem, MailListCard } from '@dbrain/shared';
import { Avatar, Icons, toneOf } from '@dbrain/ui';
import { Fragment, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  attachmentExtent,
  attachmentMeta,
  attachmentSource,
  attendeeSummary,
  calendarChips,
  calendarDay,
  dayBadge,
  dayLabel,
  durationText,
  eventPlace,
  eventTime,
  FILE_BADGE,
  fileKind,
  mailChips,
  mailListView,
  mailTime,
  notListedText,
  pointPlace,
  restText,
  senderAddress,
  senderName,
  stripRange,
  type CalendarDayView,
  type FileKind,
  type MailGroupKind,
} from '@dbrain/app-core/answer-cards';
import { formatBytes } from '@dbrain/app-core/format';
import { parseInline, type Inline } from '@dbrain/app-core/markdown';
import { SaveAttachment, SavedNote, useAttachmentSave } from '../microsoft365/files/SaveAttachment.js';
import { OutlookIcon } from '../microsoft365/MicrosoftIcon.js';

/**
 * Microsoft 365 answer cards (Phase 15e, D-197; docs/design/mockups/MsTakvimYatay, MsMailListe, MsMailEk): a
 * day of the calendar on a strip of at least 08–18 with its numbered list, the mails in groups with Oppy's one-line
 * summaries, an attachment's summary with page chips. Every fact (sender, subject, time, attendee, file name,
 * size) came from Graph through the server and is shown as it came; the model gave only the grouping and the
 * summaries. Rows are not links and nothing leads out to Outlook; an attachment read here was not added to the
 * documents. The strip's geometry goes through React style objects (CSP: no style attributes in the markup). The
 * page draws each card in an error boundary of its own, so one card that cannot be drawn leaves the answer around it.
 * Part 3: an attachment card in a conversation has "Belgelerime kaydet" (MsMailEkKaydet).
 */
export function AnswerCardView({ card, self, conversationId }: { card: AnswerCard; self?: readonly string[]; conversationId?: string }) {
  switch (card.kind) {
    case 'calendar':
      return <CalendarCardView card={card} self={self ?? []} />;
    case 'mail_list':
      return <MailListCardView card={card} />;
    case 'attachment':
      return <AttachmentCardView card={card} conversationId={conversationId} />;
    default:
      return null;
  }
}

/** A card that could not be drawn: one quiet line in its place. */
export function AnswerCardError() {
  const { t } = useTranslation();
  return (
    <p className="answer-card-error" role="status" data-testid="answer-card-error">
      <Icons.warning size={16} />
      {t('microsoft365.chat.cardFailed')}
    </p>
  );
}

/** A summary's words with its bold and italics; a link stays words (nothing in a card leads out of Oppy). */
function inline(nodes: Inline[]): ReactNode[] {
  return nodes.map((node, index) => {
    switch (node.kind) {
      case 'text':
        return <Fragment key={index}>{node.text}</Fragment>;
      case 'code':
        return <code key={index}>{node.text}</code>;
      case 'strong':
        return <strong key={index}>{inline(node.children)}</strong>;
      case 'em':
        return <em key={index}>{inline(node.children)}</em>;
      case 'del':
        return <del key={index}>{inline(node.children)}</del>;
      case 'link':
        return <Fragment key={index}>{inline(node.children)}</Fragment>;
      default:
        return null;
    }
  });
}

const Words = ({ text }: { text: string }) => <>{inline(parseInline(text))}</>;

function Chips({ items, testId }: { items: string[]; testId: string }) {
  return (
    <ul className="answer-card__chips" data-testid={testId}>
      {items.map((chip) => (
        <li key={chip} className="answer-card__chip">
          {chip}
        </li>
      ))}
    </ul>
  );
}

/** A file's type as a small badge ("PDF", "XLS"), or a plain file icon when its kind is unknown. */
function FileBadge({ kind, large = false }: { kind: FileKind; large?: boolean }) {
  const badge = FILE_BADGE[kind];
  return (
    <span className={`file-type file-type--${kind}${large ? ' file-type--large' : ''}`} aria-hidden="true">
      {badge || <Icons.file size={large ? 18 : 12} />}
    </span>
  );
}

// ---- the calendar (MsTakvimYatay) ----

/** Geometry of the strip, as CSS variables on the element (set through the CSSOM). */
const at = (percent: number) => ({ '--at': `${percent}%` }) as CSSProperties;
const span = (start: number, width: number, lane = 0, lanes = 1) => ({ '--start': `${start}%`, '--span': `${width}%`, '--lane': lane, '--lanes': lanes }) as CSSProperties;

/** A free stretch shorter than an hour has no room for its words on the strip; the chip still names it. */
const GAP_ON_STRIP = 60;

function Strip({ view }: { view: CalendarDayView }) {
  const { t } = useTranslation();
  const { range } = view;
  const gap = view.gap && view.gap.minutes >= GAP_ON_STRIP ? view.gap : null;
  // the list under it says the same in words: the strip is a picture
  return (
    <div className="cal-strip" aria-hidden="true" data-testid="calendar-strip" data-from={range.from / 60} data-to={range.to / 60}>
      <div className="cal-strip__hours">
        {range.hours
          .filter((hour) => hour.label)
          .map((hour) => (
            <span key={hour.hour} className={hour.minor ? 'cal-strip__hour cal-strip__hour--minor' : 'cal-strip__hour'} style={at(hour.at)}>
              {String(hour.hour).padStart(2, '0')}
            </span>
          ))}
      </div>
      <div className="cal-strip__track">
        {range.hours.map((hour) => (
          <span key={hour.hour} className="cal-strip__line" style={at(hour.at)} />
        ))}
        {gap ? (
          <span className="cal-strip__gap" style={span(gap.start, gap.span)} data-testid="calendar-gap">
            {t('microsoft365.chat.calendar.free', { duration: durationText(gap.minutes, t) })}
          </span>
        ) : null}
        {view.blocks.map((block) => (
          <span
            key={block.ref}
            className={`cal-strip__event cal-strip__event--${block.online ? 'online' : 'room'}`}
            style={span(block.start, block.span, block.lane, block.lanes)}
            title={block.subject}
            data-testid="calendar-block"
            data-n={block.n}
          >
            {block.n}
          </span>
        ))}
      </div>
    </div>
  );
}

function CalendarDay({ view, timeZone, self, now }: { view: CalendarDayView; timeZone: string; self: readonly string[]; now: Date }) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const badge = dayBadge(view.date, i18n.language);
  return (
    <section className="cal-day" aria-labelledby={titleId} data-testid="calendar-day" data-date={view.date}>
      <div className="cal-day__head">
        <span className="cal-day__badge" aria-hidden="true">
          <span className="cal-day__month">{badge.month}</span>
          <span className="cal-day__number">{badge.day}</span>
        </span>
        <span className="answer-card__titles">
          <span className="cal-day__label" id={titleId}>
            {dayLabel(view.date, timeZone, now, i18n.language, t)}
          </span>
          <span className="answer-card__subtitle">{t('microsoft365.chat.calendar.source')}</span>
        </span>
        <Chips items={calendarChips(view, t)} testId="calendar-chips" />
      </div>
      {view.blocks.length > 0 ? <Strip view={view} /> : null}
      {view.events.length > 0 ? (
        <ol className="cal-list" start={view.events[0]?.n} aria-label={t('microsoft365.chat.calendar.list')}>
          {view.events.map((event) => {
            const place = eventPlace(event, t);
            const kind = event.online ? 'online' : 'room';
            return (
              <li
                key={event.ref}
                className={event.isCancelled ? 'cal-event cal-event--cancelled' : 'cal-event'}
                data-testid="calendar-event"
                data-ref={event.ref}
                data-n={event.n}
              >
                <span className={`cal-event__n cal-event__n--${kind}`} aria-hidden="true">
                  {event.n}
                </span>
                <span className="cal-event__time">{eventTime(event, timeZone, t)}</span>
                <span className="cal-event__subject">
                  <span className="cal-event__title">{event.subject}</span>
                  {event.isCancelled ? <span className="cal-event__cancelled">{t('microsoft365.chat.calendar.cancelled')}</span> : null}
                </span>
                <span className="cal-event__place">
                  {place ? (
                    <>
                      {place.kind === 'online' ? <Icons.video size={13} /> : <Icons.location size={13} />}
                      {place.text}
                    </>
                  ) : null}
                </span>
                <span className="cal-event__people">{attendeeSummary(event, t, self)}</span>
              </li>
            );
          })}
        </ol>
      ) : null}
    </section>
  );
}

function CalendarCardView({ card, self }: { card: CalendarCard; self: readonly string[] }) {
  const now = new Date();
  // one range for the card: the days' strips line up hour under hour
  const range = stripRange(card.days, card.timeZone);
  return (
    <div className="answer-card answer-card--calendar" data-testid="answer-card" data-kind="calendar">
      {card.days.map((day) => (
        <CalendarDay key={day.date} view={calendarDay(day, card.timeZone, range, card.days)} timeZone={card.timeZone} self={self} now={now} />
      ))}
    </div>
  );
}

// ---- the mails (MsMailListe) ----

/** A mail as MsMailListe draws it; the write cards reuse it for the mail they answer (part 3). */
export function MailRow({ item, time, waiting }: { item: MailCardItem; time: string; waiting: boolean }) {
  const { t, i18n } = useTranslation();
  const name = senderName(item.from);
  return (
    <li className={item.isRead ? 'mail-row' : 'mail-row mail-row--unread'} data-testid="mail-row" data-ref={item.ref} data-unread={item.isRead ? undefined : ''}>
      {item.isRead ? null : (
        <span className="mail-row__dot">
          <span className="ui-visually-hidden">{t('microsoft365.chat.mail.unread')}</span>
        </span>
      )}
      <Avatar name={name} tone={toneOf(item.from.address || name)} size="lg" className="mail-row__avatar" />
      <div className="mail-row__body">
        <div className="mail-row__top">
          <span className="mail-row__from" title={senderAddress(item.from)}>
            {name}
          </span>
          <span className="mail-row__time">{time}</span>
        </div>
        <span className="mail-row__subject">{item.subject}</span>
        {item.summary ? (
          <span className="mail-row__summary">
            <Words text={item.summary} />
          </span>
        ) : null}
        {waiting || item.attachments.length > 0 ? (
          <span className="mail-row__tags">
            {waiting ? <span className="mail-tag mail-tag--waiting">{t('microsoft365.chat.mail.waiting')}</span> : null}
            {item.attachments.map((a, n) => (
              <span key={`${a.name}-${n}`} className="mail-tag" title={formatBytes(a.size, i18n.language)}>
                <Icons.attach size={12} />
                {a.name}
              </span>
            ))}
          </span>
        ) : null}
      </div>
    </li>
  );
}

function MailGroup({ kind, items, time }: { kind: MailGroupKind; items: MailCardItem[]; time: (item: MailCardItem) => string }) {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <div className="mail-group" data-testid="mail-group" data-kind={kind}>
      <div className="mail-group__title" id={titleId}>
        <span className="mail-group__name">{t(`microsoft365.chat.mail.groups.${kind}`)}</span>
        <span className="mail-group__hint">{t(`microsoft365.chat.mail.groupHints.${kind}`, { count: items.length })}</span>
      </div>
      <ul className="mail-list" aria-labelledby={titleId}>
        {items.map((item) => (
          <MailRow key={item.ref} item={item} time={time(item)} waiting={kind === 'reply'} />
        ))}
      </ul>
    </div>
  );
}

function MailListCardView({ card }: { card: MailListCard }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const restId = useId();
  const view = mailListView(card, t);
  const notListed = notListedText(view, t);
  // a plain list (v0.15e.1: the model wrote no groups because the person only asked for a list): its rows are the card
  const plain = view.groups.length === 0;
  const now = new Date();
  const time = (item: MailCardItem) => mailTime(item.receivedAt, view.sameDay, card.timeZone, i18n.language, now);
  return (
    <section className="answer-card answer-card--mail" aria-label={t('microsoft365.chat.mail.label', { title: view.title })} data-testid="answer-card" data-kind="mail_list">
      <div className="answer-card__band">
        {/* Microsoft's icon as it comes, with "Outlook" in the source line beside it (D-201) */}
        <OutlookIcon size={20} />
        <span className="answer-card__titles">
          <span className="answer-card__title">{view.title}</span>
          {view.subtitle ? <span className="answer-card__subtitle">{view.subtitle}</span> : null}
        </span>
        <Chips items={mailChips(view, t)} testId="mail-chips" />
      </div>
      {view.groups.map((group) => (
        <MailGroup key={group.kind} kind={group.kind} items={group.items} time={time} />
      ))}
      {open || plain ? (
        <ul className={plain ? 'mail-list' : 'mail-list mail-list--rest'} id={restId} data-testid="mail-rest">
          {view.rest.items.map((item) => (
            <MailRow key={item.ref} item={item} time={time(item)} waiting={false} />
          ))}
        </ul>
      ) : null}
      {(view.rest.count > 0 && !plain) || notListed ? (
        <div className="answer-card__foot mail-card__foot">
          {view.rest.count > 0 && !plain ? (
            <>
              <span className="mail-card__rest" data-testid="mail-rest-summary">
                {restText(view, t)}
              </span>
              <button type="button" className="mail-card__toggle" aria-expanded={open} aria-controls={open ? restId : undefined} onClick={() => setOpen(!open)} data-testid="mail-rest-toggle">
                {t(open ? 'microsoft365.chat.mail.hide' : 'microsoft365.chat.mail.show')}
              </button>
            </>
          ) : null}
          {/* what Graph counted but did not list: nothing to open, so it is said without the fold */}
          {notListed ? (
            <span className="mail-card__not-listed" data-testid="mail-not-listed">
              {notListed}
            </span>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// ---- an attachment (MsMailEk) ----

function AttachmentCardView({ card, conversationId }: { card: AttachmentCard; conversationId?: string }) {
  const { t, i18n } = useTranslation();
  const summaryId = useId();
  const kind = fileKind(card.attachment.name, card.attachment.contentType);
  const slides = attachmentExtent(card.attachment)?.kind === 'slides';
  const now = new Date();
  // part 3: "Belgelerime kaydet" (MsMailEkKaydet); attachment cards come from eki_oku, which reads file attachments only
  const save = useAttachmentSave(conversationId, card.attachment.ref);
  return (
    <section className="answer-card answer-card--attachment" aria-label={t('microsoft365.chat.attachment.label', { name: card.attachment.name })} data-testid="answer-card" data-kind="attachment">
      <div className="answer-card__band att-card__head">
        <FileBadge kind={kind} large />
        <span className="answer-card__titles">
          <span className="answer-card__title att-card__name">{card.attachment.name}</span>
          <span className="answer-card__subtitle" data-testid="attachment-meta">
            {attachmentMeta(card.attachment, i18n.language, t)}
          </span>
          <span className="att-card__source" data-testid="attachment-source">
            {/* Microsoft's icon right before the product's name, as in the mail card's band (D-201) */}
            <OutlookIcon size={16} />
            {attachmentSource(card, i18n.language, t, now)}
          </span>
        </span>
        {save.offered ? <SaveAttachment save={save} sender={senderName(card.message.from).split(' · ')[0]?.trim() ?? ''} /> : null}
      </div>
      {card.points.length > 0 ? (
        <div className="att-card__summary">
          <span className="att-card__summary-title" id={summaryId}>
            {t('microsoft365.chat.attachment.summary')}
          </span>
          <ul className="att-card__points" aria-labelledby={summaryId}>
            {card.points.map((point, n) => {
              const place = pointPlace(point.pages, t, slides);
              return (
                <li key={n} className="att-point" data-testid="attachment-point">
                  <span className="att-point__text">
                    <Words text={point.text} />
                  </span>
                  {place ? (
                    <span className="att-point__page" data-testid="attachment-page">
                      {place}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
      <div className="answer-card__foot att-card__foot">
        {save.place ? (
          <SavedNote place={save.place} />
        ) : (
          <span className="att-card__note">
            <Icons.info size={14} />
            {t('microsoft365.chat.attachment.onlyHere')}
          </span>
        )}
        {card.siblings.length > 0 ? (
          <span className="att-card__siblings" data-testid="attachment-siblings">
            {t('microsoft365.chat.attachment.siblings', { count: card.siblings.length })}
            {card.siblings.map((sibling, n) => (
              <span key={`${sibling.name}-${n}`} className="att-card__sibling">
                <FileBadge kind={fileKind(sibling.name, sibling.contentType)} />
                <strong>{sibling.name}</strong>
                <span>· {formatBytes(sibling.size, i18n.language)}</span>
              </span>
            ))}
          </span>
        ) : null}
      </div>
    </section>
  );
}
